import {
    memo,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type ReactNode
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import {
    DataGridPremium,
    type GridColDef,
    type GridRenderCellParams
} from '@mui/x-data-grid-premium';
import {
    selectConstants,
    selectCountries
} from 'features/general/selectors';
import {
    deletePspKycSetting
} from 'features/pspKycSettings/api';
import { resetPspKycSettings } from 'features/pspKycSettings/slice';
import type { PspKycSetting } from 'features/pspKycSettings/types';
import { useAppDispatch } from 'hooks/store';
import { useTypedSelector } from 'hooks';
import Iconify from 'lib/iconify';
import NoDataOverlay from 'ui/molecules/TableGrids/Table/NoDataOverlay';
import DeleteKycDialog from '../KycSettingsCard/DeleteKycDialog';
import useFetchPspKycSettings from '../useFetchPspKycSettings';
import {
    formatCountryLabel,
    formatKycProviderLabel,
    formatKycTypeLabel,
    formatMethodLabel,
    formatMorLabel,
    formatPspLabel
} from './formatters';

type Row = {
    readonly setting: PspKycSetting;
    readonly psp: string;
    readonly mor: string;
    readonly method: string;
    readonly kycProvider: string;
    readonly kycType: string;
    readonly country: string;
};

type Props = {
    readonly action?: ReactNode;
};

const gridSx = {
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#212B36',
        fontWeight: 600
    }
};

const PspKycSettingsTable = ({ action }: Props) => {
    const dispatch = useAppDispatch();
    const { getSettingsSlice, isLoading } = useFetchPspKycSettings();
    const constants = useTypedSelector(selectConstants);
    const countries = useTypedSelector(selectCountries());

    const [search, setSearch] = useState('');
    const [pendingDelete, setPendingDelete] = useState<PspKycSetting | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const wasDeletingRef = useRef(false);

    const countryByIso = useMemo(
        () => new Map<string, string>(
            countries.map(country => [country.iso2, country.shortName])
        ),
        [countries]
    );

    const kycTypesMap = constants.KycTypesMap ?? {};

    const rows: Array<Row> = getSettingsSlice().map(setting => ({
        setting,
        psp: formatPspLabel(setting),
        mor: formatMorLabel(setting),
        method: formatMethodLabel(setting),
        kycProvider: formatKycProviderLabel(setting),
        kycType: formatKycTypeLabel(setting, kycTypesMap),
        country: formatCountryLabel(setting, countryByIso)
    }));

    const normalizedSearch = search.trim().toLowerCase();
    const filteredRows = normalizedSearch
        ? rows.filter(row =>
            [row.psp, row.mor, row.method, row.kycProvider, row.kycType, row.country]
                .some(value => value?.toLowerCase().includes(normalizedSearch))
        )
        : rows;

    useEffect(() => {
        if (wasDeletingRef.current && !isDeleting) {
            setPendingDelete(null);
        }
        wasDeletingRef.current = isDeleting;
    }, [isDeleting]);

    const onSearchChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setSearch(target.value);
    };

    const openDelete = (setting: PspKycSetting) => setPendingDelete(setting);
    const closeDelete = () => {
        if (isDeleting) return;
        setPendingDelete(null);
    };

    const confirmDelete = async () => {
        if (!pendingDelete || isDeleting) {
            return;
        }
        setIsDeleting(true);
        try {
            const response = await deletePspKycSetting(pendingDelete.coreId);
            const json = await response.json().catch(() => ({} as Record<string, unknown>));
            const success = (json as { success?: boolean })?.success;
            const code = (json as { code?: number })?.code;
            const isErrorEnvelope = success === false
                || (typeof code === 'number' && code >= 400);
            if (!response.ok || isErrorEnvelope) {
                throw new Error(`Request failed with status ${response.status}`);
            }
            dispatch(resetPspKycSettings());
        } catch {
        } finally {
            setIsDeleting(false);
        }
    };

    const columns: GridColDef[] = [
        { field: 'psp', headerName: 'PSP Provider', flex: 1, minWidth: 160 },
        { field: 'mor', headerName: 'MOR', flex: 1, minWidth: 140 },
        { field: 'method', headerName: 'Method', flex: 1, minWidth: 140 },
        { field: 'kycProvider', headerName: 'KYC Provider', flex: 1, minWidth: 140 },
        { field: 'kycType', headerName: 'KYC Type', flex: 1, minWidth: 140 },
        { field: 'country', headerName: 'Country', flex: 1, minWidth: 140 },
        {
            field: 'actions',
            headerName: 'Action',
            width: 130,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }: GridRenderCellParams<unknown, Row>) => (
                <Button
                    size='small'
                    startIcon={<Iconify icon='solar:trash-bin-trash-bold' />}
                    onClick={() => openDelete(row.setting)}
                    data-test-id={`pspKycSettings.delete.${row.setting.coreId}`}
                    sx={{ color: '#7A0916' }}
                >
                    Delete
                </Button>
            )
        }
    ];

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2
                }}
            >
                <TextField
                    type='search'
                    size='small'
                    placeholder='Search PSPs'
                    value={search}
                    onChange={onSearchChange}
                    disabled={isLoading}
                    sx={{ width: 320 }}
                    inputProps={{ 'data-test-id': 'pspKycSettings.search' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' />
                            </InputAdornment>
                        )
                    }}
                />
                {action}
            </Box>

            <Box sx={{ width: '100%' }}>
                <DataGridPremium
                    autoHeight
                    rows={filteredRows}
                    columns={columns}
                    getRowId={(row) => String((row as Row).setting.coreId)}
                    loading={isLoading}
                    disableSelectionOnClick
                    density='compact'
                    pagination
                    rowsPerPageOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            pageSize: 10
                        }
                    }}
                    pinnedColumns={{ right: ['actions'] }}
                    components={{ NoRowsOverlay: NoDataOverlay }}
                    sx={gridSx}
                />
            </Box>

            <DeleteKycDialog
                open={Boolean(pendingDelete)}
                pspName={pendingDelete?.PSP ?? ''}
                isProcessing={isDeleting}
                onConfirm={confirmDelete}
                onClose={closeDelete}
            />
        </>
    );
};

export default memo(PspKycSettingsTable);
